import { ViewChild } from '@angular/core';
import { Component, Input, OnInit } from '@angular/core';
import { Guid } from 'guid-typescript';
import { combineLatest } from 'rxjs';
import { DataInfo, discardChanges, EntriesCollection, OfferSection, Product, remove, saveChanges } from '../shared';
import { OperationSectionService } from './service/operation-section.service';

@Component({
  selector: 'app-operation-section',
  templateUrl: './operation-section.component.html',
  styleUrls: ['./operation-section.component.scss']
})
export class OperationSectionComponent implements OnInit {
  @Input() projectId: Guid;
  @ViewChild('operationAndMaintenanceTable') public operationAndMaintenanceTable: { startEdit: () => void; };
  @ViewChild('itTable') public itTable: { startEdit: () => void; };
  operationAndMaintenanceServices: ReadonlyArray<Product>;
  itServices: ReadonlyArray<Product>;
  private operationAndMaintenanceSectionInfo: DataInfo<OfferSection>;
  private itSectionInfo: DataInfo<OfferSection>;
  private internalIsBusy: boolean;

  get isBusy(): boolean {
    return this.internalIsBusy;
  }

  get hasOperationAndMaintenanceSection(): boolean {
    return !!this.operationAndMaintenanceSectionInfo.data;
  }

  get operationAndMaintenanceSection(): OfferSection {
    return this.operationAndMaintenanceSectionInfo.data;
  }

  get hasItSection(): boolean {
    return !!this.itSectionInfo.data;
  }

  get itSection(): OfferSection {
    return this.itSectionInfo.data;
  }

  constructor(private service: OperationSectionService) {
    this.operationAndMaintenanceSectionInfo = {data: undefined, isNew: false };
    this.itSectionInfo = {data: undefined, isNew: false };
    this.service.isBusy$.subscribe(isBusy => this.internalIsBusy = isBusy);
  }

  ngOnInit(): void {
    const operationAndMaintenanceServices$ = this.service.getOperationAndMaintenanceServices();
    const operationAndMaintenanceSection$ = this.service.getOperationAndMaintenanceSection(this.projectId);
    combineLatest([operationAndMaintenanceServices$, operationAndMaintenanceSection$])
      .subscribe(([operationAndMaintenanceServices, operationAndMaintenanceSection]) => {
        this.operationAndMaintenanceServices = operationAndMaintenanceServices;
        this.operationAndMaintenanceSectionInfo.data = operationAndMaintenanceSection;
      });

    const itServices$ = this.service.getItServices();
    const itSection$ = this.service.getItSection(this.projectId);
    combineLatest([itServices$, itSection$])
      .subscribe(([itServices, itSection]) => {
        this.itServices = itServices;
        this.itSectionInfo.data = itSection;
      });
  }

  initializeOperationAndMaintenanceSection(): void {
    this.operationAndMaintenanceSectionInfo.isNew = true;
    this.operationAndMaintenanceSectionInfo.data = {
      id: Guid.create(),
      createdOn: new Date(),
      productRelatedEntries: [],
      customEntries: []
    };
    this.operationAndMaintenanceTable.startEdit();
  }

  saveOperationAndMaintenanceSectionChanges(entriesCollection: EntriesCollection): void {
    const updatedSection = {
      id: this.operationAndMaintenanceSectionInfo.data.id,
      createdOn: this.operationAndMaintenanceSectionInfo.data.createdOn,
      ...entriesCollection
    };
    saveChanges(this.projectId, updatedSection, this.operationAndMaintenanceSectionInfo,
      this.service.addOperationAndMaintenanceSection.bind(this.service),
      this.service.updateOperationAndMaintenanceSection.bind(this.service));
  }

  discardOperationAndMaintenanceSectionChanges(): void {
    discardChanges(this.operationAndMaintenanceSectionInfo);
  }

  deleteOperationAndMaintenanceSection(): void {
    remove(this.projectId, this.operationAndMaintenanceSectionInfo,
      this.service.deleteOperationAndMaintenanceSection.bind(this.service));
  }

  initializeItSection(): void {
    this.itSectionInfo.isNew = true;
    this.itSectionInfo.data = {
      id: Guid.create(),
      createdOn: new Date(),
      productRelatedEntries: [],
      customEntries: []
    };
    this.itTable.startEdit();
  }

  saveItSectionChanges(entriesCollection: EntriesCollection): void {
    const updatedSection = {
      id: this.itSectionInfo.data.id,
      createdOn: this.itSectionInfo.data.createdOn,
      ...entriesCollection
    };
    saveChanges(this.projectId, updatedSection, this.itSectionInfo,
      this.service.addItSection.bind(this.service),
      this.service.updateItSection.bind(this.service));
  }

  discardItSectionChanges(): void {
    discardChanges(this.itSectionInfo);
  }

  deleteItSection(): void {
    remove(this.projectId, this.itSectionInfo,
      this.service.deleteItSection.bind(this.service));
  }
}
